import React from "react";
import dompurify from "dompurify";
import { Card, Flex, Text, Button } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { DisplayMessage } from "../../hooks/useChatMessages";
import { isURL, formatTimestamp } from "../../utils";
import { MessageLoadingIndicator } from "./MessageLoadingIndicator";
import * as styles from "./styles_vanilla.css";

export type MessageProps = {
  message: DisplayMessage;
  isChatInProgress: boolean;
  isLastAssistantMessage: boolean;
  onFollowupClick: (question: string) => void;
  onFeedbackClick?: (feedback: boolean | null) => void;
};

export const Message: React.FC<MessageProps> = ({
  message: {
    id,
    role,
    content,
    createdAt,
    isLoading,
    thinkingText,
    followupQuestions,
    feedback,
    summary,
  },
  onFollowupClick,
  isChatInProgress,
  onFeedbackClick,
  isLastAssistantMessage,
}) => {
  const isUser = role === "user";
  const isUrlContent = isURL(content);
  const shouldShowFollowupQuestions =
    isLastAssistantMessage && followupQuestions && followupQuestions.length > 0;

  const handleFeedbackClick = (newFeedback: boolean) => {
    const feedbackValue = feedback === newFeedback ? null : newFeedback;
    onFeedbackClick?.(feedbackValue);
  };

  const renderMessageContent = () => {
    if (isLoading) {
      return <MessageLoadingIndicator thinkingText={thinkingText} />;
    }

    if (isUrlContent) {
      return (
        <Card className={styles.messageUrlContainer} data-testid="message-url-container">
          <Flex justifyContent="space-between" alignItems="center" gap={Sizes.SM}>
            <Text variant="secondary-md" className={styles.messageUrlText}>
              Here is the chart
            </Text>
            <Icon type="chart-line" size="medium" color={Colors.Success600} />
          </Flex>
        </Card>
      );
    }

    if (summary) {
      const sanitizedSummary = dompurify.sanitize(summary);
      return (
        <div
          data-testid="message-summary"
          dangerouslySetInnerHTML={{ __html: sanitizedSummary }}
        />
      );
    }

    return (
      <Text variant="regular-md" className={styles.messageText} data-testid="message-text">
        {content}
      </Text>
    );
  };

  return (
    <Flex
      gap={Sizes.MD}
      className={isUser ? styles.messageUserContainer : styles.messageAssistantContainer}
      data-testid={isUser ? "message-user" : "message-assistant"}
    >
      {!isUser && (
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles.messageAvatar}
          data-testid="message-avatar-assistant"
        >
          <Icon type="sparkles" size="medium" />
        </Flex>
      )}
      <Flex flexDirection="column" gap={Sizes.XS} className={styles.messageContentContainer}>
        {isLoading ? (
          renderMessageContent()
        ) : (
          <Flex className={isUser ? styles.messageUserBubble : styles.messageAssistantBubble}>
            {renderMessageContent()}
          </Flex>
        )}
        <Flex alignItems="center" gap={Sizes.SM}>
          <Text
            variant="secondary-sm"
            className={styles.messageTimestamp}
            data-testid="message-timestamp"
          >
            {formatTimestamp(createdAt)}
          </Text>
          {!isLoading && !isUser && onFeedbackClick && id && (
            <Flex gap={Sizes.XS} data-testid="message-feedback-buttons">
              <Button
                icon="thumbs-up"
                variant="muted"
                onClick={() => handleFeedbackClick(true)}
                className={
                  feedback === true ? styles.feedbackButtonActive : styles.feedbackButton
                }
                data-testid="feedback-thumbs-up"
              />
              <Button
                icon="thumbs-down"
                variant="muted"
                onClick={() => handleFeedbackClick(false)}
                className={
                  feedback === false ? styles.feedbackButtonActive : styles.feedbackButton
                }
                data-testid="feedback-thumbs-down"
              />
            </Flex>
          )}
        </Flex>
        {!isLoading && !isUser && shouldShowFollowupQuestions && (
          <Flex
            flexDirection="column"
            gap={Sizes.XS}
            data-testid="followup-questions"
            className={styles.followupQuestionsContainer}
          >
            <Text variant="secondary-sm">Suggested follow-up questions:</Text>
            {followupQuestions?.map((question, index) => (
              <Button
                key={`${question}-${index}`}
                variant="neutral"
                onClick={() => onFollowupClick(question)}
                className={styles.followupQuestionButton}
                disabled={isChatInProgress}
                data-testid={`followup-question-${index}`}
              >
                {question}
              </Button>
            ))}
          </Flex>
        )}
      </Flex>
      {isUser && (
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles.messageAvatar}
          data-testid="message-avatar-user"
        >
          <Icon type="user" size="medium" />
        </Flex>
      )}
    </Flex>
  );
};
